import { IconButton, Link, MessageBar, MessageBarButton, MessageBarType } from '@fluentui/react';
import * as React from 'react';
import { captureContentUpdate } from '../../../../web/utils/oneDsUtil';
import { Device } from '../../../models/apiModels';
import { OutdatedDevicesAlertConstants, IconNames, OutdatedDevicesAlertEnabledComponent } from '../../../models/constants/constants';
import { IOutdatedDevicesAlertStyles, getOutdatedDevicesAlertClassNames } from '../styles/outdatedDevicesAlertStyles';
import { IStyledComponent } from '../styles/themesUI';
import { OutdatedDevice } from './OutdatedDevice';
import { getString } from '../../../../web/utils/stringLoader';

interface OutdatedDevicesAlertProps {
  devices: Array<Device>;
  shouldShowOutdatedDevicesAlert: boolean;
  isOutdatedDevicesAlertDismissed: boolean;
  shouldExpandOutdatedDevicesAlert: boolean;
  callingComponent: OutdatedDevicesAlertEnabledComponent;
  deviceMinCountToShowExpandIcon: number;
  deviceMaxCount: number;
  howToUpdateWindowsUrl: string;
  windowsUpdateInvokingUrl: string;
  showAlert: boolean;
  dismissOutdatedDevicesAlert: (comp: OutdatedDevicesAlertEnabledComponent) => void;
  toggleOutdatedDevicesAlertExpand: (comp: OutdatedDevicesAlertEnabledComponent) => void;
}

type IOutdatedDevicesAlertProps = OutdatedDevicesAlertProps & IStyledComponent<IOutdatedDevicesAlertStyles>;

export class OutdatedDevicesAlert extends React.Component<IOutdatedDevicesAlertProps> {
  public constructor(props: IOutdatedDevicesAlertProps) {
    super(props);
  }

  public componentDidMount = (): void => {
    this.logTheAlert('componentDidMount');
  };

  public componentDidUpdate = (): void => {
    this.logTheAlert('componentDidUpdate');
  };

  public render() {
    const devices = this.props.devices;
    const showAlert = this.props.showAlert;
    const isAlertCollapsible = !!devices && this.isAlertCollapsible(devices.length);
    const shouldExpandAlert = this.props.shouldExpandOutdatedDevicesAlert;
    const expandAlert = !isAlertCollapsible || shouldExpandAlert;
    const area = this.props.callingComponent === OutdatedDevicesAlertEnabledComponent.ModalitySection
    ? OutdatedDevicesAlertConstants.area.modalitySection : OutdatedDevicesAlertConstants.area.selfHelpAlchemy;
    const classNames = getOutdatedDevicesAlertClassNames(this.props.styles, { theme: this.props.theme });
    return (showAlert &&
      <div className={classNames.outdatedDevicesAlertContainer} >
        <MessageBar
          className={classNames.outdatedDeviceAlert}
          messageBarType={MessageBarType.info}
          aria-label={getString('outdatedDeviceAlert_alertAriaLabelText')}
          onDismiss={this.dismissAlert}
          dismissButtonAriaLabel={getString('outdatedDeviceAlert_dismissAlertText')}
        >
          <div className={classNames.outdatedDeviceAlertMain} >
            <div className={classNames.outdatedDeviceAlertMessage} >{
              this.props.callingComponent === OutdatedDevicesAlertEnabledComponent.ModalitySection
              ? getString('outdatedDeviceAlert_alertMessageModalitySection')
              : getString('outdatedDeviceAlert_alertMessageSelfHelpAlchemy')
            }</div>
            {isAlertCollapsible &&
              <IconButton className={classNames.outdatedDeviceAlertExpandIcon}
                id={shouldExpandAlert ? OutdatedDevicesAlertConstants.contentIds.closeIcon : OutdatedDevicesAlertConstants.contentIds.expandIcon}
                data-bi-area={area}
                data-bi-formnm={OutdatedDevicesAlertConstants.formnm}
                onClick={this.toggleExpand}
                title={shouldExpandAlert ? getString('outdatedDeviceAlert_closeDeviceListText') : getString('outdatedDeviceAlert_checkDeviceListText')}
                aria-label={shouldExpandAlert ? getString('outdatedDeviceAlert_closeDeviceListText') : getString('outdatedDeviceAlert_checkDeviceListText')}
                iconProps={{ iconName: shouldExpandAlert ? IconNames.DoubleChevronUp : IconNames.DoubleChevronDown }}
              />
            }
          </div>
          <div className={classNames.outdatedDevicelist} >
            {expandAlert && devices && devices.slice(0, this.props.deviceMaxCount).map(device =>
              <OutdatedDevice key={device.Id} device={device} outdatedDeviceStatusText={getString('outdatedDeviceAlert_devicesStatusText')} />
            )}
          </div>
          <div className={classNames.outdatedDeviceSuggestions} >
            {!expandAlert &&
              <Link onClick={this.toggleExpand}
                className ={classNames.expandAlertLink + ' ' + classNames.overflowTextElement}
                id={OutdatedDevicesAlertConstants.contentIds.expandLink}
                data-bi-area={area}
                data-bi-formnm={OutdatedDevicesAlertConstants.formnm}>
                  {getString('outdatedDeviceAlert_checkDeviceListText')}</Link>}
            {expandAlert &&
              <Link onClick={this.toggleExpand} style={{ visibility: isAlertCollapsible ? 'visible' : 'hidden' }}
                className = {classNames.closeAlertLink + ' ' + classNames.overflowTextElement}
                id={OutdatedDevicesAlertConstants.contentIds.closeLink}
                data-bi-area={area}
                data-bi-formnm={OutdatedDevicesAlertConstants.formnm}>
                  {getString('outdatedDeviceAlert_closeDeviceListText')}</Link>}
            {expandAlert &&
              <Link href={this.props.howToUpdateWindowsUrl} target="_blank"
                className ={classNames.windowsUpdateArticleLink + ' ' + classNames.overflowTextElement}
                id={OutdatedDevicesAlertConstants.contentIds.windowsUpdateArticleLink}
                data-bi-area={area}
                data-bi-formnm={OutdatedDevicesAlertConstants.formnm}>
                  {getString('outdatedDeviceAlert_updateOtherDevicesText')}</Link>}
            {expandAlert &&
              <MessageBarButton className={classNames.updateDeviceButton + ' ' + classNames.overflowTextElement} onClick={this.updateWindows}
              title={getString('outdatedDeviceAlert_updateCurrentDeviceText')}
              id={OutdatedDevicesAlertConstants.contentIds.windowsUpdateInvokerButton}
                data-bi-area={area}
                data-bi-formnm={OutdatedDevicesAlertConstants.formnm}>
                  {getString('outdatedDeviceAlert_updateCurrentDeviceText')}</MessageBarButton>}
          </div>
        </MessageBar>
      </div>
    );
  }

  private dismissAlert = () => this.props.dismissOutdatedDevicesAlert(this.props.callingComponent);

  private toggleExpand = () => this.props.toggleOutdatedDevicesAlertExpand(this.props.callingComponent);

  private updateWindows = () => window.open(this.props.windowsUpdateInvokingUrl, '_self');

  // The list will be expanded when it's not collapsible
  private isAlertCollapsible(deviceCount: number) {
    return Math.min(deviceCount, this.props.deviceMaxCount) >= this.props.deviceMinCountToShowExpandIcon;
  }

  private logTheAlert(invokingMethod: string) {
    const devicesCount = !!this.props.devices ? this.props.devices.length : 0;
    if (devicesCount > 0) {
      const area = this.props.callingComponent === OutdatedDevicesAlertEnabledComponent.ModalitySection
      ? OutdatedDevicesAlertConstants.area.modalitySection : OutdatedDevicesAlertConstants.area.selfHelpAlchemy;
      const isAlertExpanded = !this.isAlertCollapsible(devicesCount) || this.props.shouldExpandOutdatedDevicesAlert;
      const alertContent = {
        content: {
          contentId: OutdatedDevicesAlertConstants.contentIds.alert,
          formnm: OutdatedDevicesAlertConstants.formnm,
          logTrigger: invokingMethod,
          shouldShowAlert: this.props.shouldShowOutdatedDevicesAlert,
          isAlertDismissed: this.props.isOutdatedDevicesAlertDismissed,
          isAlertExpanded: isAlertExpanded,
          deviceMinCountForCollapsibleAlert: this.props.deviceMinCountToShowExpandIcon,
          deviceCount: devicesCount,
          devices: this.props.devices.map(device => {
            return { id: device.Id, osVersion: device.OsVersion, manufacturer: device.Manufacturer };
          }),
          areaName: area,
        },
        behavior: 160,
      };
      captureContentUpdate(alertContent);
    }
  }
}
